import { t } from "i18next";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import ColumnPersonInformationAvatar from "../molecules/ColumnPersonInformationAvatar";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { TableLoader } from "../molecules/TableLoader";
import ColumnText from "../molecules/ColumnText";
import { Select } from "@/shared/components/atoms/Select";
import { Exercise } from "@/app/exercises/exercises";
import { FormikProps } from "formik";
import {
  equipmentOptions,
  exerciseTypeOptions,
  muscleOptions,
} from "@/app/training/training";
import { ExerciseType } from "@/app/training/exercise-type";

export type TableListExercisesProps = {
  exercises: Exercise[];
  search?: string;
  onChange: FormikProps<any>["handleChange"];
  equipment: string;
  muscle: string;
  type: string;
  onEdit: (exercise: any) => void;
  onDelete: (exercise: any) => void;
  isLoading: boolean;
};

const TableListExercises: React.FC<TableListExercisesProps> = ({
  onChange,
  exercises,
  search,
  equipment,
  muscle,
  type,
  onEdit,
  onDelete,
  isLoading,
}) => {
  return (
    <div className="flex flex-col h-full">
      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="w-full">
          <InputWithCornerHint
            name="search"
            type="text"
            placeholder={t("user.views.exercise")}
            label={t("general.search")}
            error={false}
            onChange={onChange}
            value={search}
          />
        </div>
        <div className="w-full">
          <Select
            className="w-full"
            name="equipment"
            value={equipment}
            optional
            onChange={onChange}
            options={equipmentOptions}
            label={t("user.views.type_equipment")}
          />
        </div>
        <div className="w-full">
          <Select
            className="w-full"
            name="type"
            optional
            options={exerciseTypeOptions}
            value={type}
            onChange={onChange}
            label={t("accounting.views.type")}
          />
        </div>
      </div>
      <div className="border-b border-gray-300 mb-4" />

      {/* Tabla con scroll horizontal en móvil */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          {isLoading ? (
            <TableLoader columns={4} rows={14} />
          ) : (
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 w-1/3"
                  >
                    {t("user.views.exercise")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/4"
                  >
                    {t("user.views.type_exercise")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/3"
                  >
                    {t("user.views.link_youtube")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 w-[100px]"
                  >
                    {t("general.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {exercises.map((exercise, index) => (
                  <tr key={index}>
                    <td className="py-4 pl-4 pr-3 sm:pl-0">
                      <div className="flex items-center gap-3">
                        {exercise.imageUrl && (
                          <img
                            src={exercise.imageUrl}
                            alt={exercise.exercise}
                            className="h-12 w-12 object-cover"
                          />
                        )}
                        <div className="min-h-[2.5rem] line-clamp-2">
                          <p className="text-sm font-medium text-gray-900">
                            {exercise.exercise}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      <div className="min-h-[2.5rem] line-clamp-2 whitespace-normal">
                        {exercise.exercise}
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      <div className="min-h-[2.5rem] line-clamp-2 whitespace-normal">
                        {exercise.videoUrl}
                      </div>
                    </td>
                    <td className="px-3 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => onEdit(exercise)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          {t("general.edit")}
                        </button>
                        <button
                          onClick={() => onDelete(exercise)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableListExercises;
